import React, { useEffect, useState } from "react";

import { Box, CompassColor, Image } from "@noom/wax-component-library";

import { useTrackEvent } from "@/hooks";

import { FadeIn, FadeInAndOut } from "./animation/fade";

const expandingCircleSx = {
  "@keyframes open": {
    from: {
      height: 0,
      width: 0,
      top: "50%",
      left: "50%",
      opacity: 0,
    },
    to: {
      height: "300vh",
      width: "300vh",
      top: "calc(50% - 150vh)",
      left: "calc(50% - 150vh)",
      opacity: 1,
    },
  },
  animation: "open 2s 3.5s forwards",
};

const containerSx = {
  "& h1": {
    fontFamily: "\"Brown LL\", sans-serif",
    fontStyle: "normal",
    fontWeight: 500,
    letterSpacing: "0.03em",
    color: CompassColor.white,
    fontSize: "14px",
    lineHeight: "21px",
    textTransform: "uppercase",
    textAlign: "center",
    marginBottom: "24px",
  },
};

const logoSx = {
  "@keyframes slideUpSmall": {
    from: {
      opacity: 0,
      transform: "translate(0, 20px)",
    },
    to: {
      opacity: 1,
      transform: "translate(0, 0)",
    },
  },
  animation: "slideUpSmall 0.5s 2s forwards",
};

const slideContainerProps = {
  position: "fixed" as const,
  height: "100vh",
  paddingTop: "30vh",
  width: "100%",
  display: "flex",
  flexDirection: "column" as const,
};

const slideTextProps = {
  fontFamily: "\"Untitled Serif\", sans-serif",
  fontStyle: "normal" as const,
  fontWeight: 500,
  color: CompassColor.black,
  fontSize: "24px",
  textAlign: "center" as const,
  margin: "24px",
};

type SlideType = {
  content: React.ReactElement;
  canProceed?: () => boolean;
};

const WelcomeToNoomAnimation: React.FC<{
  animationDone: () => void;
  isEligible?: boolean;
}> = ({ animationDone, isEligible }) => {
  const { trackAnonPageViewed, trackIdentifiedPageViewed } = useTrackEvent();

  useEffect(() => {
    trackAnonPageViewed("ssoEligibilityVerification");
    trackIdentifiedPageViewed("ssoEligibilityVerification");
  }, []);

  const [currentSlideIdx, setCurrentSlideIdx] = useState<number>(0);
  const slides: SlideType[] = [
    {
      content: (
        <Box {...slideContainerProps} backgroundColor={CompassColor.blueberry}>
          <FadeIn duration={0.5} delay={1}>
            <h1>WELCOME TO</h1>
          </FadeIn>
          <Image
            src="/assets/img/noomLogo.svg"
            alt="Noom"
            opacity={0}
            height="36px"
            sx={logoSx}
          />
          <Box
            position="fixed"
            backgroundColor={CompassColor.offWhite}
            borderRadius="50%"
            opacity={0}
            sx={expandingCircleSx}
            onAnimationEnd={() => {
              setCurrentSlideIdx(currentSlideIdx + 1);
            }}
          />
        </Box>
      ),
    },
    {
      content: (
        <Box {...slideContainerProps} backgroundColor={CompassColor.offWhite}>
          <FadeInAndOut
            delay={0}
            duration={2}
            onAnimationEnd={() => {
              if (!isEligible) {
                animationDone();
              }
              setCurrentSlideIdx(currentSlideIdx + 1);
            }}
          >
            <Image
              alt="checkmark"
              margin="0 auto"
              src="/assets/img/greenCheckmarkInCircle.svg"
              visibility="hidden"
            />
            <Box {...slideTextProps}>
              Verifying eligibility information...
            </Box>
          </FadeInAndOut>
        </Box>
      ),
    },
    {
      content: (
        <Box {...slideContainerProps} backgroundColor={CompassColor.offWhite}>
          <FadeInAndOut
            delay={2}
            onAnimationEnd={() => {
              animationDone();
            }}
          >
            <Image
              alt="checkmark"
              margin="0 auto"
              src="/assets/img/greenCheckmarkInCircle.svg"
            />
            <Box {...slideTextProps}>Eligibility verified</Box>
          </FadeInAndOut>
        </Box>
      ),
    },
  ];

  return (
    <Box
      position="fixed"
      left={0}
      top={0}
      display="flex"
      flexDirection="column"
      alignItems="center"
      height="100vh"
      width="100vw"
      backgroundColor={CompassColor.blueberry}
      sx={containerSx}
    >
      {slides[currentSlideIdx].content}
    </Box>
  );
};

export default WelcomeToNoomAnimation;
